import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { finalize } from 'rxjs/operators';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { getStatus, isPackageAllowed } from 'utils/packages/packageState';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { translate } from 'components/Translation';
import AccordionContent from './AccordionContent';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { SERVICE_STATUS } from '../../constants';

interface ISimpleAccordionContentWrapperProps {
	showOnlyActive: boolean;
	type?: types.ecommerce.PackageType;
	group?: types.ecommerce.PackageGroup;
	termsAndConditions?: types.services.termsAndConditions;
	updateService?: (singlePackage: types.services.IPackage) => void;
	confirmations?: types.services.IConfirmation[];
	description: (singlePackage: SinglePackage) => string;
	name: (singlePackage: SinglePackage) => string;
	note?: string;
	cannotBeTurnedOff?: string[];
	activationDisabled?: boolean;
	notesOnTop?: boolean;
	setPendingStatus?: (status: boolean) => void;
	subscriberProducts: SingleSubscriberProduct[];
}

const SimpleAccordionContentWrapper = ({
	showOnlyActive,
	type,
	group,
	termsAndConditions,
	updateService,
	confirmations,
	description,
	name,
	note,
	cannotBeTurnedOff,
	activationDisabled,
	notesOnTop = false,
	subscriberProducts,
}: ISimpleAccordionContentWrapperProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [packages, setPackages] = useState<SinglePackage[]>([]);
	const isMounted = useRef(true);

	useEffect(() => {
		if (type && group) {
			setLoading(true);
			ECommerceService.getPackages(type, group)
				.pipe(
					finalize(() => {
						if (isMounted.current) {
							setLoading(false);
						}
					})
				)
				.subscribe(({ packages }) => {
					if (isMounted.current) {
						setPackages(packages);
					}
				});
		}
	}, [group, type]);

	const extendedPackages: types.services.IPackage[] = useMemo(() => {
		return packages
			.filter((singlePackage: SinglePackage) => isPackageAllowed(subscriberProducts, singlePackage))
			.map((singlePackage: SinglePackage) => {
				const getPackageStatus = getStatus(subscriberProducts, singlePackage);
				const buttonHidden =
					(getPackageStatus === SERVICE_STATUS.ACTIVE &&
						(cannotBeTurnedOff || []).indexOf(singlePackage.partNumber) >= 0) ||
					activationDisabled;
				const subscriberProduct: SingleSubscriberProduct | undefined = subscriberProducts.find(
					({ productId }) => productId === getProductId(singlePackage.partNumber)
				);
				return {
					name: name(singlePackage),
					description: description(singlePackage),
					legacyProductId: singlePackage.partNumber,
					place: singlePackage.place,
					id: singlePackage.uniqueID,
					roamingType: singlePackage.roamingType,
					ebuConfigProduct: singlePackage.ebuConfigProduct,
					status: {
						type: getPackageStatus,
						date: getPackageStatus !== SERVICE_STATUS.ACTIVE ? subscriberProduct?.startDate : undefined,
					},
					buttons: buttonHidden
						? []
						: [
								{
									text:
										getPackageStatus === SERVICE_STATUS.ACTIVE
											? translate('numbers.services.turn.off')
											: translate('numbers.services.turn.on'),
									color:
										getPackageStatus === SERVICE_STATUS.ACTIVE ? 'tertiary-light' : 'primary-light',
									disabled:
										getPackageStatus == SERVICE_STATUS.PENDING_ACTIVATION ||
										getPackageStatus == SERVICE_STATUS.PENDING_TERMINATION ||
										getPackageStatus == SERVICE_STATUS.PENDING,
									type: getPackageStatus === SERVICE_STATUS.ACTIVE ? 'TURN_OFF' : 'TURN_ON',
								},
						  ],
				};
			});
	}, [packages, description, name, cannotBeTurnedOff, activationDisabled, subscriberProducts]);

	const filteredPackages = useMemo(() => {
		return showOnlyActive
			? extendedPackages.filter(
					(singlePackage: types.services.IPackage) =>
						!singlePackage.status || singlePackage.status?.type === 'ACTIVE'
			  )
			: extendedPackages;
	}, [showOnlyActive, extendedPackages]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<ConditionalRender
			show={loading}
			onTrue={() => <CustomCircularProgress text={translate('number.loading.packages')} loading={loading} />}
			onFalse={() => (
				<AccordionContent
					elements={filteredPackages}
					termsAndConditions={termsAndConditions}
					updateService={updateService}
					confirmations={confirmations}
					note={note}
					notesOnTop={notesOnTop}
				/>
			)}
		/>
	);
};

export default SimpleAccordionContentWrapper;
